import moment from 'moment';
import { SCHEDULED, isScheduledOrder } from 'shared/models/order';
import { availabilityForAddress, modifyOrder } from 'shared/models/api';
import { sanitizeAvailability } from 'shared/models/address';

const GIFTCLAIM_AVAILABILITY_LOADING = 'GIFTCLAIM_AVAILABILITY_LOADING';
const GIFTCLAIM_AVAILABILITY_ERROR = 'GIFTCLAIM_AVAILABILITY_ERROR';
const GIFTCLAIM_AVAILABILITY_SUCCESS = 'GIFTCLAIM_AVAILABILITY_SUCCESS';
const GIFTCLAIM_ORDER_UPDATED = 'GIFTCLAIM_ORDER_UPDATED';
const GIFTCLAIM_DATE_SELECTED = 'GIFTCLAIM_DATE_SELECTED';
const GIFTCLAIM_SLOT_SELECTED = 'GIFTCLAIM_SLOT_SELECTED';
const GIFTCLAIM_ETA_SELECTED = 'GIFTCLAIM_ETA_SELECTED';
const GIFTCLAIM_SCHEDULED_SELECTED = 'GIFTCLAIM_SCHEDULED_SELECTED';
const GIFTCLAIM_SHOW_DOWNLOAD_MODAL = 'GIFTCLAIM_SHOW_DOWNLOAD_MODAL';
const GIFTCLAIM_HIDE_DOWNLOAD_MODAL = 'GIFTCLAIM_HIDE_DOWNLOAD_MODAL';

const init = {
  availabilityLoading: false,
  availabilityError: null,
  canDeliverASAP: false,
  canScheduleLater: false,
  etaDisplay: '',
  etaSelectionText: '',
  dateOptions: [],
  contactNumber: null,
  order: null,
  date: null,
  slot: null,
  selectedETA: null,
  showDownloadModal: false
};

export default function reducer(state = init, action) {
  switch (action.type) {
    case GIFTCLAIM_AVAILABILITY_LOADING:
      return {
        ...state,
        availabilityLoading: true
      };
    case GIFTCLAIM_AVAILABILITY_SUCCESS:
      return {
        ...state,
        availabilityLoading: false,
        availabilityError: null,
        canDeliverASAP: !!action.data.immediate_delivery,
        canScheduleLater: !!action.data.scheduling,
        etaDisplay: action.data.eta.display,
        etaSelectionText: action.data.selection_text,
        dateOptions: action.data.dates
      };
    case GIFTCLAIM_AVAILABILITY_ERROR:
      return {
        ...state,
        availabilityLoading: false,
        availabilityError: action.error,
        canDeliverASAP: false,
        canScheduleLater: false,
        etaDisplay: '',
        etaSelectionText: '',
        dateOptions: []
      };
    case GIFTCLAIM_ORDER_UPDATED:
      return { ...state, order: { ...state.order, ...action.updatedFields } };
    case GIFTCLAIM_DATE_SELECTED:
      return { ...state, date: action.date };
    case GIFTCLAIM_SLOT_SELECTED:
      return { ...state, selectedETA: null, slot: action.slot };
    case GIFTCLAIM_ETA_SELECTED:
      return { ...state, selectedETA: action.eta, slot: null };
    case GIFTCLAIM_SCHEDULED_SELECTED:
      return { ...state, selectedETA: null };
    case GIFTCLAIM_SHOW_DOWNLOAD_MODAL:
      return { ...state, showDownloadModal: true };
    case GIFTCLAIM_HIDE_DOWNLOAD_MODAL:
      return { ...state, showDownloadModal: false };
    default:
      return state;
  }
}

export function fetchAvailability() {
  return (dispatch, getState) => {
    const { order } = getState().giftClaim || {};
    const { lat, lng } = order.delivery_address_detail || {};
    if (!lat || !lng) return Promise.resolve();

    dispatch({ type: GIFTCLAIM_AVAILABILITY_LOADING });

    return availabilityForAddress({ lat, lng })
      .then(res => {
        const data = sanitizeAvailability(res);
        const { dates, eta } = data;

        dispatch({ type: GIFTCLAIM_AVAILABILITY_SUCCESS, data });

        if (isScheduledOrder(order)) {
          if (order.scheduled) {
            const date =
              dates &&
              dates.find(({ date }) => {
                return date === moment(order.scheduled).format('MM/DD/YYYY');
              });

            const slot =
              date &&
              date.slots &&
              date.slots.find(({ start }) => {
                return (
                  moment(start).format('HH:mm') ===
                  moment(order.scheduled).format('HH:mm')
                );
              });

            if (date && slot) {
              dispatch(selectDate(date));
              dispatch(selectSlot(slot));
              return;
            }
          }

          if (eta && eta.display) {
            dispatch(selectETA(eta.display));
          }
        }
      })
      .catch(err => {
        console.error(err); // eslint-disable-line no-console
        dispatch({
          type: GIFTCLAIM_AVAILABILITY_ERROR,
          error: 'Something went wrong. Please go back and try again.'
        });
      });
  };
}

export function updateDeliveryNotes(delivery_notes) {
  return dispatch =>
    dispatch({
      type: GIFTCLAIM_ORDER_UPDATED,
      updatedFields: { delivery_notes }
    });
}

export function updateUnit(unit) {
  return (dispatch, getState) => {
    const order = getState().giftClaim.order;
    const delivery_address_detail = { ...order.delivery_address_detail, unit };
    dispatch({
      type: GIFTCLAIM_ORDER_UPDATED,
      updatedFields: { delivery_address_detail }
    });
  };
}

export function selectDate(date) {
  return dispatch =>
    dispatch({
      type: GIFTCLAIM_DATE_SELECTED,
      date
    });
}

export function selectSlot(slot) {
  return dispatch =>
    dispatch({
      type: GIFTCLAIM_SLOT_SELECTED,
      slot
    });
}

export function selectETA(eta) {
  return dispatch => dispatch({ type: GIFTCLAIM_ETA_SELECTED, eta });
}

export function selectScheduled() {
  return dispatch => dispatch({ type: GIFTCLAIM_SCHEDULED_SELECTED });
}

export function setThankYouNote(note) {
  return dispatch =>
    dispatch({
      type: GIFTCLAIM_ORDER_UPDATED,
      updatedFields: { recipient_note: note }
    });
}

export function confirmOrder() {
  return (dispatch, getState) => {
    const { order, slot } = getState().giftClaim;

    const updatedFields = {
      order_status: SCHEDULED,
      scheduled: slot ? slot.start : null
    };

    dispatch({
      type: GIFTCLAIM_ORDER_UPDATED,
      updatedFields
    });

    return modifyOrder({ ...order, ...updatedFields });
  };
}

export function openDownloadModal() {
  return dispatch => dispatch({ type: GIFTCLAIM_SHOW_DOWNLOAD_MODAL });
}

export function closeDownloadModal() {
  return dispatch => dispatch({ type: GIFTCLAIM_HIDE_DOWNLOAD_MODAL });
}
